import { FC } from 'react';
import { z } from 'zod';
import { Grid, MenuItem, OutlinedInput } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormField } from '../../form/FormField';
import { ControlledFormField } from '../../form/ControlledFormField';
import { graphql } from '../../gql';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { useModalContext } from '../../modal/ModalContext';
import { JourneyUpdatePhasesEntityRelationInput } from '../../gql/graphql';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { ModalContent } from '../../modal/components/ModalContent';
import { ModalActions } from '../../modal/components/ModalActions';
import { PhaseSelect } from '../PhaseSelect';
import { useTypedForm } from '../../../../shared/fe/hooks/useForm';
import { useSnackbar } from '../../store/snackbar';

const journeyCreatePhaseModal_createPhaseAndReorder = graphql(`
	mutation journeyCreatePhaseModal_createPhaseAndReorder(
		$journeyId: UUID!
		$phaseName: String!
		$phaseDesc: String!
		$order: Int!
		$phases: [JourneyUpdatePhasesEntityRelationInput!]!
	) {
		createPhase(data: { name: $phaseName, description: $phaseDesc, order: $order, journey: { connect: { id: $journeyId } } }) {
			ok
			errorMessage
		}
		updateJourney(by: { id: $journeyId }, data: { phases: $phases }) {
			ok
			errorMessage
		}
	}
`);

const journeyCreatePhaseModal_getJourney = graphql(`
	query journeyCreatePhaseModal_getJourney($id: UUID!) {
		getJourney(by: { id: $id }) {
			id
			phases {
				id
			}
		}
	}
`);

const createPhaseModalSchema = z.object({
	id: z.string(),
	phaseId: z.string().optional(),
});

type Props = z.infer<typeof createPhaseModalSchema>;

const formSchema = z.object({
	name: z.string(),
	afterPhaseId: z.string(),
});

type FormValues = z.infer<typeof formSchema>;

const CreatePhaseModal: FC<Props> = ({ id: journeyId, phaseId: afterPhaseId }) => {
	const intl = useIntl();
	const { closeModal } = useModalContext();
	const { mutate: createPhaseAndReorder, isPending: isCreatePending } = useApiMutation(journeyCreatePhaseModal_createPhaseAndReorder);
	const { data: journey, isPending: isJourneyPending } = useApiQuery(journeyCreatePhaseModal_getJourney, { variables: { id: journeyId } });
	const { openSnackbar } = useSnackbar();

	const form = useTypedForm({
		schema: formSchema,
		defaultValues: {
			name: '',
			afterPhaseId: afterPhaseId || '',
		},
	});

	const submitDisabled = !form.formState.isValid || isJourneyPending || isCreatePending;

	const onSubmit = (values: FormValues) => {
		const phases = journey?.getJourney?.phases;
		if (phases == null) {
			// NOTE: this should not happen, since submit button is disabled by `isJourneyLoading`
			throw new Error('Journey is not loaded.');
		}

		let phasesChange: JourneyUpdatePhasesEntityRelationInput[];
		let order: number;

		if (values.afterPhaseId === '') {
			phasesChange = [];
			order = phases.length;
		} else {
			if (values.afterPhaseId === '_begin') {
				order = 0;
			} else {
				order = phases.findIndex((phase) => phase.id === values.afterPhaseId) + 1;
			}

			phasesChange = phases.map((phase, index) => {
				return {
					update: {
						by: {
							id: phase.id,
						},
						data: { order: index < order ? index : index + 1 },
					},
				};
			});
		}

		createPhaseAndReorder(
			{
				journeyId,
				phaseName: values.name || intl.formatMessage({ id: 'common.journey.phase.name' }),
				phaseDesc: intl.formatMessage({ id: 'common.journey.phase.description' }),
				order,
				phases: phasesChange,
			},
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.createPhase.ok && result.updateJourney.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'common.journey.phase.success.created' }),
							error: intl.formatMessage({ id: 'common.journey.phase.error.create' }),
						},
						errorMessage: result.createPhase.errorMessage || result.updateJourney.errorMessage,
						callback: closeModal,
						openSnackbar,
					});
				},
			},
		);
	};

	return (
		<>
			<ModalContent title={intl.formatMessage({ id: 'form.phase.title.create' })} description={''}>
				<form noValidate>
					<Grid container spacing={3}>
						<FormField
							form={form}
							name={'name'}
							label={intl.formatMessage({ id: 'form.phase.label.name' })}
							render={(props) => <OutlinedInput placeholder={intl.formatMessage({ id: 'form.phase.placeholder.name' })} {...props} />}
						/>

						<ControlledFormField
							form={form}
							name={'afterPhaseId'}
							label={intl.formatMessage({ id: 'form.phase.label.phase' })}
							render={(props) => (
								<PhaseSelect
									journeyId={journeyId}
									{...props}
									prependItems={
										<MenuItem value={'_begin'}>
											<i>
												<FormattedMessage id={'form.phase.item.add-at-beginning'} />
											</i>
										</MenuItem>
									}
								/>
							)}
						/>
					</Grid>
				</form>
			</ModalContent>
			<ModalActions
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={form.handleSubmit(onSubmit)}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={intl.formatMessage({ id: 'form.phase.title.submit' })}
				disablePrimaryButton={submitDisabled}
			/>
		</>
	);
};

export { CreatePhaseModal, createPhaseModalSchema };
